import {
    AccountServiceInfo,
    AccountServiceStatusDateDto,
    AccountServiceStatusForDisplay,
    ActiveMeterStatus,
    DefaultDisplayDateFormat,
    DefaultDisplayMonthFormat,
    EuropaAccountServiceStatus, 
    GetTemperatureRequest,
    MeterReadBasicModel,
    MeterReadMeter,
    MeterReadRequest,
    MeterReadSmartModel,
    Site, 
    TemperatureDataDto,
    ServiceType,
    CostDetailModel,
    CostDetailRequest,
    ChargeCategoryCode,
    ConsumptionAndExportChargeCategory,
    DefaultJsonDateFormat
} from 'data/CustomerPortalTypes';
import { useEffect, useState, useRef, useCallback } from 'react';
import {useSelector} from 'react-redux';
import {
    downLoadCsvFile,
    getAccountServiceStatusDateAsync,
    getBasicMeterRead,
    getMeterListFromRead, 
    getPowerMeterTypes,
    getSmartMeterRead,
    getWeatherData,
    getCostDetail,
} from 'services/RequestDataService';
import {selectCurrentAccount} from 'store/userSlice';
import styles from './UsageChart.module.css';
import { useNavigate } from 'react-router-dom';
import { Select, DatePicker, Checkbox } from 'antd';
import ReactECharts from 'echarts-for-react';
import {cloneDeep} from 'lodash';
import moment, {Moment} from 'moment';
import {PanelMode, RangeValue} from 'rc-picker/lib/interface';
import MenuOptions from '../MenuOptions';
import SiteSelector from 'components/Shared/SiteSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { shouldBeDisabledByDayDateRange, today, toSite, toUtcStartOfDay } from 'utilities/Utility';
import links from 'data/Links';
import { Radio } from 'antd';
import type { RadioChangeEvent } from 'antd';

enum TimeGranularityEnum {
    Day = 'Day',
    Month = 'Month',
    Year = 'Year',
    day = 'day',
    month = 'month',
    year = 'year',
    date = 'date',
    Hour = "Hour",
    HalfHour = "HalfHour"
}

enum ChartTypeEnum {
    Usage = "usage",
    Cost = "cost"
}

type Series = {
    name: string;
    type: string;
    stack: string | null;
    data: any[],
    yAxisIndex?: number,
    symbolSize?: number,
    symbol?: string,
    lineStyle?: {},
    emphasis: object | null,
    silent: boolean,
    itemStyle: object | null
}
type Legend = {
    show: boolean,
    top: number | null,
    data: Array<string>
}

type ClickedBarModel = {
    offsetX: number,
    date: string,
    totalCost: number,
    energyCost: number,
    supplyCharge: number,
    solarFeedIn: number,
    superExport: number,
    zeroHero: number,
    criticalPeakExport: number,
    criticalPeakImport: number,
    display: string,
    displaySolar: string
}
const checkIsCrossAccountService = (svc: AccountServiceInfo) => svc
    .allAccountServiceIds
    .map(sId => accountServiceFrmpDateMap.map?.[sId])
    .filter(s => s?.frmpDate)
    .length > 1;

// const values
const DateFormatYMD = "YYYY-MM-DD";
const ServiceDateFormatYMD = "YYYY/MM/DD";
const DateFormatYM = "YYYY-MM";
const DateFormatDM = "DD-MMM"
const DateFormatY = "YYYY";
const DateFormatYMDHm = "YYYY-MM-DD HH:mm";
const DateFormatHm = "HH:mm";
// Australian standard date formats
const DateFormatDMY = "DD-MM-YYYY";
const DateFormatMY = "MM-YYYY";
const DateFormatDMYHm = "DD-MM-YYYY HH:mm";
const HourTypeArr = [TimeGranularityEnum.Hour, TimeGranularityEnum.HalfHour];
const HourTypeMinutes = {
    [TimeGranularityEnum.Hour]: 60,
    [TimeGranularityEnum.HalfHour]: 30,
    30: TimeGranularityEnum.HalfHour,
    60: TimeGranularityEnum.Hour
};
const { RangePicker } = DatePicker;
const { Option } = Select;
const MinutesADay = 1440;
const MinsecondsADay = 3600 * 24 * 1000;
const MinsecondsAHour = 60 * 60 * 1000;
const MinsecondsAHalfHour = 30 * 60 * 1000;
const weatherSeriesName = ['Min Temperature', 'Max Temperature'];

const DefaultOptions = {
    title: {
        text: '',
        left: 'center',
        textStyle: {
            color: '#333333',
            fontWeight: 'bold',
            fontSize: 16,
        }
    },
    color: [
        '#F73809', '#0909F7', '#09F7F7', '#FF00FF',
        '#FFFF00', '#00FF33', '#FAD860', '#F3A43B',
        '#60C0DD', '#D7504B', '#C6E579', '#F4E001',
        '#F0805A', '#26C0C0'],
    dataZoom: new Array<any>(),
    tooltip: {
        trigger: 'axis',
        triggerOn: 'mousemove',
        position: null as null | Array<number> | Array<string>,
        formatter: function (params: string | any[]) {
            var htmlStr = '';
            for (var i = 0; i < params.length; i++) {
                var param = params[i];
                var value = param.value;//[x,y]
                var xName = safeMoment(value[0]).format(DateFormatDMY);//x
                var seriesName = param.seriesName;
                var color = param.color;

                if (i === 0) {
                    htmlStr += xName + '<br/>';
                }
                htmlStr += '<div>';

                htmlStr += '<span style="margin-right:5px;display:inline-block;width:10px;height:10px;border-radius:5px;background-color:' + color + ';"></span>';
                htmlStr += seriesName + ': ' + value[1];

                htmlStr += '</div>';
            }
            return htmlStr;
        }
    },
    legend: [{
        show: true,
        top: 30,
        data: new Array<string>()
    }] as Array<Legend>,
    grid: {
        top: null as number | null,
        left: '30px',
        right: '30px',
        bottom: '2%',
        containLabel: true
    },
    xAxis: {
        type: "time",
        min: null as (Date | null),
        max: null as (Date | null),
        minInterval: MinsecondsADay,
        splitLine: {
            show: false
        },
        boundaryGap: ['0%', '0%'],
        axisLabel: {
            formatter: (value: any, index: number) => safeMoment(value).format(DateFormatDMY),
            rotate: 0
        },
        data: new Array<string>()
    },
    yAxis: [{
        scale: true,
        type: 'value',
        name: "",
        min: null as (number | null),
        max: null as (number | null),
        nameTextStyle: {
            rich: {
                a: {
                    fontSize: '8px',
                    verticalAlign: 'top'
                },
                i: {
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                }
            }
        },
        axisLabel: {},
        splitLine: {}
    }],
    series: new Array<Series>()
};
const DefaultClickedBarModel = {
    offsetX: 0,
    date: "Jan 01, 0001",
    totalCost: 0,
    energyCost: 0,
    supplyCharge: 0,
    solarFeedIn: 0,
    superExport: 0,
    zeroHero: 0,
    criticalPeakExport: 0,
    criticalPeakImport: 0,
    display: "none",
    displaySolar: ""
};
const ChargeTypeColorMap = new Map<string, string>([["ZEROHERO Credit", "#05BE87"], ["Super Export top up", "#FF8C00"], ["USAGE", "#FF008D"], ["SOLAR", "#FBAE47"], ["SUPPLY","#8E065E"], ["Critical Peak - Export", "#6C5CE7"], ["Critical Peak - Import", "#00A8E8"]]);
const MeterType = {
    BASIC: "BASIC",
    SMART: "SMART"
}
const HourTimeGranularity = [TimeGranularityEnum.Hour, TimeGranularityEnum.HalfHour];
const TimeGranularity = {
    Usage: {
        BASIC: [TimeGranularityEnum.Day],
        SMART: [TimeGranularityEnum.Year, TimeGranularityEnum.Month, TimeGranularityEnum.Day, ...HourTimeGranularity]
    },
    COST: {
        BASIC: [TimeGranularityEnum.Month],
        SMART: [TimeGranularityEnum.Month, TimeGranularityEnum.Day]
    }
}
const BasicMeterReadDataType = {
    InvoiceReads: "Invoice Read Data",
    ActualReads: "Distributor Read/Self Read Data"
}

const requestCache = {
    meterSeted: true,
    rangeSeted: true
}

const safeMoment = (date: number | string | Date | null | undefined | Moment) => {
    if (typeof date === 'string' && date.split("-").length > 0) {
        date.replace(/-/g, "/");
    }
    return moment(date);
}

const mapSort = (map: Map<string, Series>) => {
    const sortedMap = new Map<string, Series>();
    const keys = Array.from(map.keys());
    const keysValue = new Map<string, number>([["SUPPLY", 1], ["USAGE", 2], ["ZEROHERO Credit", 3], ["SOLAR", 4], ["Super Export top up", 5], ["Critical Peak - Export", 6], ["Critical Peak - Import", 7]]);
    keys.sort((a, b) => {
        let sortValueA = keysValue.get(a)!;
        let sortValueB = keysValue.get(b)!;
        return sortValueA - sortValueB;
    });

    keys.forEach(x => {
        sortedMap.set(x, map.get(x)!)
    })

    return sortedMap;
}

const emptyBasicReads = {
    [BasicMeterReadDataType.InvoiceReads]: new Array<MeterReadBasicModel>(),
    [BasicMeterReadDataType.ActualReads]: new Array<MeterReadBasicModel>(),
} as Record<string, Array<MeterReadBasicModel>>;
const emptySmartReads = new Array<MeterReadSmartModel>();
const emptyRange = null as RangeValue<moment.Moment>;
const EndOfTodayMoment = moment().endOf('day');

const emptyCost = new Array<CostDetailModel>();

type AccountServiceFrmpDateMap = {
    map: null | Record<number, AccountServiceStatusDateDto>,
    callback: null | Function
}
const accountServiceFrmpDateMap: AccountServiceFrmpDateMap = {
    map: null,
    callback: null
};

const activeServiceStatus: EuropaAccountServiceStatus[] = [EuropaAccountServiceStatus.PendingSwitchOut, EuropaAccountServiceStatus.Switched];

const activeMeterStatus: ActiveMeterStatus[] = [ActiveMeterStatus.Turnedon, ActiveMeterStatus.Energized]

const METER_STATUS_DISPLAY_MAP: Record<string, string> = {
    'Energized': 'Energised',
    'Deenergized': 'Deenergised'
};

let smartReadCache = emptySmartReads;
let basicReadCache = emptyBasicReads;
let hasUserSelectedDate = false;
let optionsCache = cloneDeep(DefaultOptions);
let weatherDataCache: Array<TemperatureDataDto> = [];

let costCache = emptyCost;

// functions
const momentSort = (a: string | Date, b: string | Date) => safeMoment(a).isBefore(safeMoment(b)) ? -1 : 1;
const dateKey = (date: string | Date) => safeMoment(date).format(DateFormatYMD);
const getPickerType = (timeGranularity: string): Exclude<PanelMode, 'decade' | 'time'> => {
    switch (timeGranularity) {
        case TimeGranularityEnum.Year:
            return TimeGranularityEnum.year;
        case TimeGranularityEnum.Month:
            return TimeGranularityEnum.month;
        default:
            return TimeGranularityEnum.date;
    }
}

const getMinutesFromTimeSpan = (timeSpan: string) => {
    let hmsArr = timeSpan.split(":");
    let hourArr = hmsArr[0].split(".");
    return hourArr.length > 1
        ? MinutesADay * parseInt(hourArr[0]) + parseInt(hourArr[1]) * 60 + parseInt(hmsArr[1])
        : parseInt(hmsArr[0]) * 60 + parseInt(hmsArr[1]);
}

const getPickerFormat = (timeGranularity: string): string | undefined => {
    switch (timeGranularity) {
        case TimeGranularityEnum.Day:
            return DefaultDisplayDateFormat;
        case TimeGranularityEnum.Month:
            return DefaultDisplayMonthFormat;
        case TimeGranularityEnum.Year:
            return undefined;
        default:
            return DefaultDisplayDateFormat;
    }
}

let dateCache = null as Moment | null;
const labelFormatter = (type: TimeGranularityEnum, date: string | Moment, index: number = -1, usedForX: boolean = false) => {
    let dateMoment = safeMoment(date);
    switch (type) {
        case TimeGranularityEnum.Year:
            return dateMoment.format(DateFormatY);
        case TimeGranularityEnum.Month:
            return dateMoment.format(DateFormatMY);
        case TimeGranularityEnum.HalfHour:
        case TimeGranularityEnum.Hour:
            return usedForX
                ? `${dateMoment.format(DateFormatHm)}\n${dateMoment.format(DateFormatDM)}\n${dateMoment.format(DateFormatY)}`
                : dateMoment.format(DateFormatDMYHm);
        default:
            if (usedForX) {
                let monthend1 = safeMoment(date).date(29).diff(dateMoment, 'day') === 0;
                let monthend2 = safeMoment(date).month() === 1 && safeMoment(date).date(28).diff(dateMoment, 'day') === 0;
                if (monthend1 || monthend2) {
                    if (index <= 1
                        || ((dateCache !== null) && safeMoment(date).date(22).diff(dateCache, 'day') === 0)) {
                        return "";
                    }
                }
                dateCache = dateMoment;
            }
            return usedForX
                ? `${dateMoment.format(DateFormatDM)}\n${dateMoment.format(DateFormatY)}`
                : dateMoment.format(DateFormatDMY);
    }
}
const setOptionsDateFormatter = (tg: TimeGranularityEnum, isSmart: boolean, chartType:string, specialEndMap: Map<string, Moment> = new Map<string, Moment>()) => {
    optionsCache.tooltip.formatter = (params: string | any[]) => {
        let htmlStr = '';
        let items = '';
        let xName = '';
        let generated = 0;
        let usageOrCost = 0;
        let currentDateData = emptySmartReads
        // let legendData = new Array<string>();
        // for(let i = 0; i < optionsCache.legend.length; i++){
        //     legendData.push(...optionsCache.legend[i].data);
        // }

        for (let i = 0; i < params.length; i++) {
            let param = params[i];
            let value = param.value;//[x,y]
            let seriesName = param.seriesName;
            let color = param.color;
            // if (legendData.indexOf(seriesName) < 0) {
            //     continue;
            // }
            if (weatherSeriesName.includes(seriesName))
            {
                if (!value?.[1]) {
                    continue;
                }
            } else {
                if (chartType === ChartTypeEnum.Usage && isSmart) {
                    let chargeCategoryCode = smartReadCache.find(r => seriesName.includes(r.chargeType))?.chargeCategoryCode
                    if (chargeCategoryCode && ConsumptionAndExportChargeCategory.includes(chargeCategoryCode as ChargeCategoryCode)) {
                        value[1] < 0
                            ? generated += value[1]
                            : usageOrCost += value[1];
                    }
                }
                else {
                    value[1] < 0
                        ? generated += value[1]
                        : usageOrCost += value[1];
                }
            }

            items += '<div>';
            items += '<span style="margin-right:5px;display:inline-block;width:10px;height:10px;border-radius:5px;background-color:' + color + ';"></span>';
            items += seriesName + (chartType === ChartTypeEnum.Usage ? ': ' :': $') + value[1];
            
            // weather station distance
            if (weatherDataCache && weatherDataCache.length > 0 && value?.[1] && value?.[2])
            {
                items += ` - Weather Station Distance: ${(value[2] / 1000).toFixed(0)} km`;
            }
            items += '</div>';
        }

        if (params.length > 0) {
            let param = params[0];
            let value = param.value;//[x,y]
            let rawDate = value[0];
            xName = labelFormatter(tg, rawDate);//x
            if (chartType === ChartTypeEnum.Usage) {
                if (isSmart && smartReadCache.length > 0) {
                    let copyList = cloneDeep(smartReadCache);
                    copyList.sort((a, b) => momentSort(a.readDate, b.readDate));
                    let start = safeMoment(copyList[0].readDate);
                    let end = safeMoment(copyList[copyList.length - 1].readDate);
                    if (tg === TimeGranularityEnum.Year) {
                        let tstart = safeMoment(rawDate).startOf('year');
                        let tend = safeMoment(rawDate).endOf('year');
                        let startStr = tstart.isBefore(start) ? start.format(DateFormatDMY) : tstart.format(DateFormatDMY);
                        let endStr = tend.isBefore(end) ? tend.format(DateFormatDMY) : end.format(DateFormatDMY);
                        xName = startStr + " to " + endStr;
                    } else if (tg === TimeGranularityEnum.Month) {
                        let tstart = safeMoment(rawDate).startOf('month');
                        let tend = safeMoment(rawDate).endOf('month');
                        let startStr = tstart.isBefore(start) ? start.format(DateFormatDMY) : tstart.format(DateFormatDMY);
                        let endStr = tend.isBefore(end) ? tend.format(DateFormatDMY) : end.format(DateFormatDMY);
                        xName = startStr + " to " + endStr;
                    } else if (HourTypeArr.indexOf(tg) > -1) {
                        let specialEnd = specialEndMap.get(xName);
                        let tend = specialEnd || safeMoment(rawDate).add(HourTypeMinutes[tg as Extract<TimeGranularityEnum.Hour, TimeGranularityEnum.HalfHour>], "minutes");
                        let startStr = xName;
                        let endStr = tend.format(DateFormatHm);
                        if (endStr === "00:00") {
                            endStr = "24:00"
                        }
                        xName = startStr + " to " + endStr;
                    }
                }
            }
            else {
                if (tg === TimeGranularityEnum.Month && isSmart && costCache.length > 0) {

                    let copyList = cloneDeep(costCache);
                    copyList.sort((a, b) => momentSort(a.date, b.date));
                    let start = safeMoment(copyList[0].date);
                    let end = safeMoment(copyList[copyList.length - 1].date);

                    let tstart = safeMoment(rawDate).startOf('month');
                    let tend = safeMoment(rawDate).endOf('month');
                    let startStr = tstart.isBefore(start) ? start.format(DateFormatDMY) : tstart.format(DateFormatDMY);
                    let endStr = tend.isBefore(end) ? tend.format(DateFormatDMY) : end.format(DateFormatDMY);
                    xName = startStr + " to " + endStr;
                }
            }
        }

        htmlStr += xName + '<br/>';
        if (isSmart) {
            if (chartType === ChartTypeEnum.Usage) {
                htmlStr += "Total Usage: " + usageOrCost.toFixed(4) + '<br/>';
                generated < 0 && (htmlStr += "Total Solar Export: " + (generated * -1).toFixed(4) + '<br/>');
            }
            else {
                htmlStr += "Total cost: $" + (usageOrCost + generated).toFixed(2) + '<br/>';                
            }
        }
        htmlStr += items;
        return htmlStr;
    };
    optionsCache.xAxis.axisLabel.formatter =
        (value: any, index: number) => labelFormatter(tg as TimeGranularityEnum, value, index, true);
}

const buildReadObj = (list: Array<MeterReadBasicModel> | Array<MeterReadSmartModel>, tg: string, isSmart: boolean)
    : [Array<MeterReadBasicModel | MeterReadSmartModel>, Map<string, Moment>]=> {
    const specialEndMap = new Map<string, Moment>();
    if (isSmart) {
        var copyList = cloneDeep(list) as Array<MeterReadSmartModel>;
        copyList.sort((a, b) => momentSort(a.readDate, b.readDate));
        let readObj = new Map<string, MeterReadSmartModel>();
        switch (tg) {
            case TimeGranularityEnum.Year: {
                for (let i = 0; i < copyList.length; i++) {
                    let key = buildLegend(copyList[i]) + safeMoment(copyList[i].readDate).year() + "";
                    if (readObj.has(key)) {
                        let read = readObj.get(key)!;
                        read.usage += copyList[i].usage;
                        read.usage = Number(read.usage.toFixed(4));
                        readObj.set(key, read)
                    } else {
                        let read = copyList[i];
                        read.readDate = safeMoment(copyList[i].readDate).startOf("year").toDate();
                        readObj.set(key, read)
                    }
                }
                optionsCache.dataZoom = [];
                optionsCache.grid.bottom = '2%';
                optionsCache.xAxis.minInterval = MinsecondsADay * 365;
                break;
            }
            case TimeGranularityEnum.Month: {
                for (let i = 0; i < copyList.length; i++) {
                    let key = buildLegend(copyList[i]) + safeMoment(copyList[i].readDate).startOf("month").format(DateFormatYM);
                    if (readObj.has(key)) {
                        let read = readObj.get(key)!;
                        read.usage += copyList[i].usage;
                        read.usage = Number(read.usage.toFixed(4));
                        readObj.set(key, read)
                    } else {
                        let read = copyList[i];
                        read.readDate = safeMoment(copyList[i].readDate).startOf("month").toDate();
                        readObj.set(key, read)
                    }
                }
                optionsCache.dataZoom = [];
                optionsCache.grid.bottom = '2%';
                optionsCache.xAxis.minInterval = MinsecondsADay * 28;
                break;
            }
            case TimeGranularityEnum.HalfHour:
            case TimeGranularityEnum.Hour: {
                let minutes = 60;
                optionsCache.xAxis.minInterval = MinsecondsAHour;
                if (tg === TimeGranularityEnum.HalfHour) {
                    minutes = 30;
                    optionsCache.xAxis.minInterval = MinsecondsAHalfHour;
                }
                if (copyList.length > 0) {
                    const bucketCoverage = new Map<string, { bucketEnd: Moment, coveredEnd: Moment }>();
                    // Adds each interval's share inside the period to its clock bucket, like billing.
                    const fillHourRead = (start: number, end: number, read: MeterReadSmartModel) => {
                        const intervalMinutes = MinutesADay / read.usageArray.length;
                        const dayStart = safeMoment(read.readDate).startOf("day");
                        const usageByBucket = new Map<number, number>();
                        const lastIndex = Math.min(Math.ceil(end / intervalMinutes), read.usageArray.length);
                        for (let index = Math.floor(start / intervalMinutes); index < lastIndex; index++) {
                            const intervalStart = index * intervalMinutes;
                            const overlap = Math.min(end, intervalStart + intervalMinutes) - Math.max(start, intervalStart);
                            if (overlap <= 0) continue;
                            const bucket = Math.floor(intervalStart / minutes);
                            usageByBucket.set(bucket, (usageByBucket.get(bucket) ?? 0) + read.usageArray[index] * overlap / intervalMinutes);
                        }
                        usageByBucket.forEach((usage, bucket) => {
                            const bucketStart = dayStart.clone().add(bucket * minutes, "minutes");
                            const bucketEnd = bucketStart.clone().add(minutes, "minutes");
                            const copyRead = cloneDeep(read);
                            copyRead.readDate = bucketStart.toDate();
                            copyRead.usage = Number(usage.toFixed(4));
                            const key = buildLegend(copyRead) + bucketStart.format(DateFormatYMDHm);
                            const existing = readObj.get(key);
                            if (existing) {
                                existing.usage = Number((existing.usage + copyRead.usage).toFixed(4));
                            } else {
                                readObj.set(key, copyRead);
                            }
                            const periodEnd = dayStart.clone().add(end, "minutes");
                            const coveredEnd = periodEnd.isBefore(bucketEnd) ? periodEnd : bucketEnd;
                            const label = labelFormatter(tg, bucketStart);
                            const coverage = bucketCoverage.get(label);
                            if (!coverage || coveredEnd.isAfter(coverage.coveredEnd)) {
                                bucketCoverage.set(label, { bucketEnd, coveredEnd });
                            }
                        });
                    }

                    for (let i = 0; i < copyList.length; i++) {
                        if (copyList[i].timePeriods) {
                            for (let j = 0; j < copyList[i].timePeriods.length; j++) {
                                let tp = copyList[i].timePeriods[j];
                                let startMinute = getMinutesFromTimeSpan(tp.startTime);
                                let endMinute = getMinutesFromTimeSpan(tp.endTime);
                                if (startMinute < endMinute) {
                                    fillHourRead(startMinute, endMinute, copyList[i]);
                                } else {
                                    fillHourRead(0, endMinute, copyList[i]);
                                    fillHourRead(startMinute, MinutesADay, copyList[i]);
                                }
                            }
                        } else {
                            fillHourRead(0, MinutesADay, copyList[i]);
                        }
                    }
                    bucketCoverage.forEach(({ bucketEnd, coveredEnd }, label) => {
                        if (coveredEnd.isBefore(bucketEnd)) {
                            specialEndMap.set(label, coveredEnd);
                        }
                    });
                    optionsCache.dataZoom = [];
                    optionsCache.grid.bottom = '2%';
                    if (copyList.length > 1) {
                        var startValue = safeMoment(copyList[0].readDate).startOf('day').toDate();
                        var endValue = safeMoment(copyList[0].readDate).startOf('day').add(24, 'hour').toDate();
                        optionsCache.dataZoom = [
                            {
                                id: 'dataZoomX',
                                type: 'slider',
                                show: true,
                                startValue,
                                endValue,
                                brushSelect: false,
                                maxValueSpan: MinsecondsADay,
                                handleSize: 8,
                                labelFormatter: (value: any) => safeMoment(value).format(DateFormatDMYHm)
                            },
                            {
                                type: 'inside',
                                startValue,
                                endValue,
                                zoomOnMouseWheel: false,
                                moveOnMouseWheel: true
                            }
                        ]
                        optionsCache.grid.bottom = '15%';
                    }
                }
                break;
            }
            default: {
                optionsCache.dataZoom = [];
                optionsCache.grid.bottom = '2%';
                optionsCache.xAxis.minInterval = MinsecondsADay;
                copyList.forEach((r: MeterReadSmartModel) => readObj.set(buildLegend(r) + dateKey(r.readDate), r));
                if (copyList.length > 1) {
                    let startValue = copyList[0].readDate;
                    let endValue = copyList[copyList.length - 1].readDate;
                    optionsCache.dataZoom = [
                        {
                            id: 'dataZoomX',
                            type: 'slider',
                            show: true,
                            startValue,
                            endValue,
                            brushSelect: true,
                            minValueSpan: MinsecondsADay,
                            handleSize: 8,
                            labelFormatter: (value: any) => safeMoment(value).format(DateFormatDMY)
                        },
                        {
                            type: 'inside',
                            startValue,
                            endValue,
                            zoomOnMouseWheel: false,
                            moveOnMouseWheel: true
                        }
                    ]
                    optionsCache.grid.bottom = '15%';
                }
                break;
            }
        }
        return [Array.from(readObj.values()), specialEndMap];
    } else {
        let copyList = cloneDeep(list) as Array<MeterReadBasicModel>;
        copyList.sort((a, b) => momentSort(a.readDate, b.readDate));
        let readObj = new Map<string, MeterReadBasicModel>();
        if (copyList.length > 1) {
            let startValue = safeMoment(copyList[0].readDate).toDate();
            let endValue = safeMoment(copyList[copyList.length - 1].readDate).toDate();
            optionsCache.dataZoom = [
                {
                    id: 'dataZoomX',
                    type: 'slider',
                    show: true,
                    startValue,
                    endValue,
                    brushSelect: true,
                    minValueSpan: MinsecondsADay,
                    handleSize: 50,
                    moveHandleSize: 12,
                    labelFormatter: (value: any) => safeMoment(value).format(DateFormatDMY)
                },
                {
                    type: 'inside',
                    startValue,
                    endValue,
                    zoomOnMouseWheel: false,
                    moveOnMouseWheel: true
                }
            ];
            optionsCache.grid.bottom = '15%';
        }
        copyList.forEach((r: MeterReadBasicModel) => readObj.set(buildLegend(r) + dateKey(r.readDate), r));
        return [Array.from(readObj.values()), specialEndMap];
    }
}

const qianhengxian = (s: string) => s ? `-${s}` : '';

const buildLegend = (r: MeterReadBasicModel | MeterReadSmartModel) => {
    let strarr = [r.suffix, r.chargeType]
    let pre = strarr.filter(s => Boolean(s)).join("-");
    if (pre) {
        return `${pre}`;
    }
    return "";
};

// const splitSeries = (list: Series[]) => {
//     let result = new Array<Series>();
//     for (let v of list) {
//         let max = 0;
//         v.data.forEach(t => max = t[1] > max ? t[1] : max);
//         max = max / 2;
//         v.data.sort((a, b) => a[0] - b[0]);
//         let t = new Array<number>();
//         t.push(0);
//         for (let i = 0; i < v.data.length - 1; i++) {
//             let x = v.data[i + 1][1] - v.data[i][1];
//             if (x < 0 && -x > max) {
//                 t.push(i + 1);
//             }
//         }
//         t.push(v.data.length);
//         for (let i = 0; i < t.length - 1; i++) {
//             let c = cloneDeep(v);
//             c.data = v.data.slice(t[i], t[i + 1]);
//             result.push(c);
//         }
//     }
//     return result;
// }

const dataProcesser = (list: Array<MeterReadBasicModel> | Array<MeterReadSmartModel>, isSmart: boolean, timeGranularity: string)
    : [string[], Series[], Map<string, Moment>] => {
    var [builtList, specialEndMap] = buildReadObj(list, timeGranularity, isSmart);
    var legends = new Map<string, Series>();
    // var groups = new Map<string, Series>();
    // var basicRegressionSeries = new Array<Series>();
    builtList.sort(
        (a: MeterReadBasicModel | MeterReadSmartModel, b: MeterReadBasicModel | MeterReadSmartModel) => momentSort(a.readDate, b.readDate));

    var barOrLine = isSmart ? "bar" : "line";
    let max = 0;
    let min = 0;
    builtList.forEach((r: MeterReadBasicModel | MeterReadSmartModel) => {
        let legend = buildLegend(r);
        let seriesData;
        if (isSmart) {
            var flag = r.suffix.startsWith("B") ? -1 : 1;
            let usage = (r as MeterReadSmartModel).usage * flag;
            seriesData = [r.readDate, usage];
            min = min < usage ? min : usage;
            max = max > usage ? max : usage;
        } else {
            seriesData = [r.readDate, (r as MeterReadBasicModel).readIndex];
            // let timeSeriesData = [(new Date(r.readDate)).getTime(), (r as MeterReadBasicModel).readIndex];
            // let key = r.suffix || "Gas";
            // groups.has(key)
            //     ? groups.get(key)!.data.push(timeSeriesData)
            //     : groups.set(key, {
            //         name: key,
            //         type: 'line',
            //         stack: key,
            //         data: [timeSeriesData],
            //         symbolSize: 0.1
            //     });
        }
        legends.has(legend)
            ? legends.get(legend)!.data.push(seriesData)
            : legends.set(legend, {
                name: legend,
                type: barOrLine,
                stack: isSmart ? "one" : null,
                data: [seriesData],
                symbolSize: 10,
                emphasis: null,
                silent: true,
                itemStyle:null
            });
    })

    if (!isSmart) {
        // basicRegressionSeries = splitSeries(Array.from(groups.values()));
        // basicRegressionSeries.forEach(s => {
        //     let t = ecStat.regression("polynomial", s.data, 1);
        //     s.data = t.points;
        // })
        optionsCache.yAxis[0].min = null;
        optionsCache.yAxis[0].max = null;
    } else {
        optionsCache.yAxis[0].min = min < 0 ? null : 0;
        optionsCache.yAxis[0].max = max > 0 ? null : 0;
    }

    const sortedLegends = mapSort(legends);
    return [Array.from(sortedLegends.keys()), Array.from(sortedLegends.values()), specialEndMap];
}

const buildCostObj = (list: Array<CostDetailModel>, tg: string) => {
    var copyList = cloneDeep(list).sort((a, b) => momentSort(a.date, b.date));
    //let amountObj = new Map<string, CostModel>();
    let categoryMap = new Map<string, Map<string, number>>();
    switch (tg) {
        case TimeGranularityEnum.Day:
            {
                for (let i = 0; i < list.length; i++) {
                    let categoryKey = copyList[i].chargeCategory;
                    let dateStringKey = dateKey(copyList[i].date);
                    if (categoryMap.has(categoryKey)) {
                        let dateMap = categoryMap.get(categoryKey)!;
                        if (dateMap.has(dateStringKey)) {
                            let amount = dateMap.get(dateStringKey)!;
                            amount += copyList[i].amount;
                            amount = Number(amount.toFixed(4));
                            dateMap.set(dateStringKey, amount)
                        }
                        else {
                            dateMap.set(dateStringKey, Number(copyList[i].amount.toFixed(4)))
                        }
                        categoryMap.set(categoryKey, dateMap);
                    }
                    else {
                        categoryMap.set(categoryKey, new Map([[dateStringKey, Number(copyList[i].amount.toFixed(4))]]))
                    }
                }
                optionsCache.xAxis.min = null;
                optionsCache.xAxis.max = null;
                optionsCache.xAxis.minInterval = MinsecondsADay;
                break;
            }
        case TimeGranularityEnum.Month:
            {
                const uniqueMonths = new Set();;
                for (let i = 0; i < list.length; i++) {
                    let categoryKey = copyList[i].chargeCategory;
                    let dateStringKey = safeMoment(copyList[i].date).startOf("month").format(DateFormatYM);
                    uniqueMonths.add(dateStringKey);
                    if (categoryMap.has(categoryKey)) {
                        let dateMap = categoryMap.get(categoryKey)!;
                        if (dateMap.has(dateStringKey)) {
                            let amount = dateMap.get(dateStringKey)!;
                            amount += copyList[i].amount;
                            amount = Number(amount.toFixed(4));
                            dateMap.set(dateStringKey, amount)
                        }
                        else {
                            dateMap.set(dateStringKey, Number(copyList[i].amount.toFixed(4)))
                        }
                        categoryMap.set(categoryKey, dateMap);
                    }
                    else {
                        categoryMap.set(categoryKey, new Map([[dateStringKey, Number(copyList[i].amount.toFixed(4))]]))
                    }
                }
                optionsCache.dataZoom = [];
                optionsCache.grid.bottom = '2%';
                optionsCache.xAxis.min = uniqueMonths.size === 1 ? safeMoment(list[0].date).toDate() : null;
                optionsCache.xAxis.max = uniqueMonths.size === 1 ? safeMoment(list[list.length - 1].date).toDate() : null;
                optionsCache.xAxis.minInterval = MinsecondsADay * 28;
                break;
            }
    }
    return categoryMap;
}

const costDataProcessor = (list: Array<CostDetailModel>, tg: TimeGranularityEnum)
    : [string[], Series[]]    => {

    var builtMap = buildCostObj(list, tg);
    var legends = new Map<string, Series>();
    builtMap.forEach((v, k, m) => {
        let seriesData = Array.from(v, ([dateString, amount]) => { return [moment(dateString, DateFormatYMD).toDate() , amount]; });
        legends.set(k, {
            name: k,
            type: "bar",
            stack: "one",
            data: seriesData,
            symbolSize: 4,
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: 'rgba(0,0,0,0.6)'
                }
            },
            silent: false,
            itemStyle: null,
            //itemStyle: {
            //    color: (param: any) => {
            //        return ChargeTypeColorMap.get(k);
            //    }
            //}
        });
    })

    const sortedLegends = mapSort(legends);
    //console.log(sortedLegends)
    return [Array.from(sortedLegends.keys()), Array.from(sortedLegends.values())];
}

const buildRequest = (
    nmi: string,
    meter: MeterReadMeter,
    tg: Exclude<PanelMode, 'decade' | 'time'>,
    range: RangeValue<moment.Moment>,
    isAcrossAccount: boolean) =>
    new MeterReadRequest(
        nmi,
        meter.serialNumber,
        range ? range[0] ? range[0]!.startOf(tg).format(ServiceDateFormatYMD) : null : null,
        range ? range[1] ? range[1]!.endOf(tg).format(ServiceDateFormatYMD) : null : null,
        meter.meterReadType === MeterType.SMART,
        isAcrossAccount);

const getMeterData = (
    accountServiceId: number,
    meterReadRequest: MeterReadRequest,
    requireEmptyResponse: boolean) => {

    if (requireEmptyResponse) {
        smartReadCache = cloneDeep(emptySmartReads);
        basicReadCache = cloneDeep(emptyBasicReads);

        return new Promise<void>((resolve) => resolve());
    }

    return meterReadRequest.isSmart
        ? new Promise<void>((resolve) => {
            getSmartMeterRead(accountServiceId, meterReadRequest)
                .then(res => res?.data
                    ? smartReadCache = cloneDeep(res.data)
                    : smartReadCache = cloneDeep(emptySmartReads))
                .catch(() => smartReadCache = cloneDeep(emptySmartReads))
                .finally(() => resolve());
        })
        : new Promise<void>((resolve) => {
            getBasicMeterRead(accountServiceId, meterReadRequest)
                .then(res => res?.data
                    ? basicReadCache = cloneDeep(res.data)
                    : basicReadCache = cloneDeep(emptyBasicReads))
                .catch(() => basicReadCache = cloneDeep(emptyBasicReads))
                .finally(() => resolve());
        });
}

const getWeatherDataRequest = async (accountServiceId: number, dateRange: RangeValue<moment.Moment>, postCode: string) => {
    const request: GetTemperatureRequest = {
        accountServiceId: accountServiceId,
        dateFrom: dateRange ? moment(dateRange?.[0]) : moment(),
        dateTo: dateRange ? moment(dateRange?.[1]) : moment(),
        postCode,
    }

    const result = await getWeatherData(request)
    weatherDataCache = result?.data || [];
}

const findMinMaxValueForUsage = (series: Series[]) => {
    const dateMap: Record<string, { feedInSum: number; chargeSum: number }> = {};
    let negativeSum = 0;
    let positiveSum = 0;

    series.forEach(x => {
        const readData = x.data;

        readData.forEach(([date,value]) => {
            if (!(date in dateMap)) {
                dateMap[date] = { feedInSum: 0, chargeSum: 0 };
            }

            if (value < 0) {
                dateMap[date].feedInSum += value;
            }
            else {
                dateMap[date].chargeSum += value;
            }
        });
    })

    for (let date in dateMap) {
        negativeSum = Math.min(negativeSum, dateMap[date].feedInSum);
        positiveSum = Math.max(positiveSum, dateMap[date].chargeSum);
    }
    
    return { negativeSum, positiveSum };
}

const findMinMaxValueForWeather = (series: Series[]) => {
    const dateMap: Record<string, { min: number; max: number }> = {};
    // Set initial min and max values to extreme values
    let overallMin = 100;
    let overallMax = -100;

    series.forEach(x => {
        const readData = x.data;

        readData.forEach(([date,value]) => {
            if (value === null) {
                return;
            }
            if (!(date in dateMap)) {
                dateMap[date] = { min: value, max: value };
            } else {
                dateMap[date].min = Math.min(dateMap[date].min, value);
                dateMap[date].max = Math.max(dateMap[date].max, value);
            }

            overallMin = Math.min(overallMin, value);
            overallMax = Math.max(overallMax, value);
        });
    });
    
    return { overallMin, overallMax }
}

const adjustProportionalScaling = (series: Series[], isMultipleChart?: boolean) => {
    const usageSeries = series.filter(x => !weatherSeriesName.includes(x.name));
    const weatherSeries = series.filter(x => weatherSeriesName.includes(x.name));

    // buffer space for the chart
    const bufferScale = 1.1;
    
    let { positiveSum: usageMax, negativeSum: usageMin } = findMinMaxValueForUsage(usageSeries);
    let { overallMax: weatherMax, overallMin: weatherMin} = findMinMaxValueForWeather(weatherSeries);
    
    usageMax = usageMax * bufferScale;
    usageMin = usageMin * bufferScale;
    weatherMax = weatherMax * bufferScale;
    weatherMin = weatherMin * bufferScale;
    
    if (isMultipleChart)
    {
        return {
            adjustedUsageSet: { min: usageMin, max: usageMax },
            adjustedWeatherSet: { min: weatherMin, max: weatherMax }
        }
    }
    
    // Ensure neither max is zero to prevent division errors
    if (usageMax === 0 || weatherMax === 0) {
        const maxValue = usageMax > weatherMax ? usageMax : weatherMax;
        const minValue = usageMin < weatherMin ? usageMin : weatherMin;
        
        return {
            adjustedUsageSet: {min: minValue, max: maxValue},
            adjustedWeatherSet: {min: minValue, max: maxValue}
        }
    }

    // Calculate proportionally adjusted min values
    const scaledUsageMin = (usageMax * weatherMin) / weatherMax;
    const scaledWeatherMin = (weatherMax * usageMin) / usageMax;

    // Enforce constraints: min can't increase, max can't decrease
    usageMin = Math.min(usageMin, scaledUsageMin);
    weatherMin = Math.min(weatherMin, scaledWeatherMin);

    // Handle case where one set is fully negative and the other is fully positive
    if (usageMin >= 0 && weatherMax <= 0) {
        usageMin = weatherMin; // Extend min of positive-only set
        weatherMax = usageMax; // Extend max of negative-only set
    } else if (weatherMin >= 0 && usageMax <= 0) {
        weatherMin = usageMin; // Extend min of positive-only set
        usageMax = weatherMax; // Extend max of negative-only set
    }
    
    return {
        adjustedUsageSet: { min: usageMin, max: usageMax },
        adjustedWeatherSet: { min: weatherMin, max: weatherMax }
    };
};

const renderWeatherData = (series: Series[], legends: string[], tg: string, isSmart: boolean, isMultipleChart: boolean = false) => {
    if (!weatherDataCache || weatherDataCache.length === 0 || tg !== TimeGranularityEnum.Day)
    {
        return;
    }

    const [invoiceReads, actualReads] = Object.values(basicReadCache);

    if (isSmart) {
        const meterReadDateSet = new Set(
            smartReadCache.map(m =>
                moment(m.readDate).format(DefaultJsonDateFormat)
            )
        );
        weatherDataCache = weatherDataCache.map(x => 
            meterReadDateSet.has(moment(x.dateAsDate).format(DefaultJsonDateFormat)) 
                ? x 
                : {...x, obMinTemp: null, obMaxTemp: null})
    } else {
        const meterReads = [...invoiceReads, ...actualReads];
        const readDates = meterReads.map(r => moment(r.readDate));
        const minReadDate = moment.min(readDates);
        const maxReadDate = moment.max(readDates);
        weatherDataCache = weatherDataCache.map(x => {
            const date = moment(x.dateAsDate);

            return (date.isBetween(minReadDate, maxReadDate, undefined, '[]'))
                ? x
                : { ...x, obMinTemp: null, obMaxTemp: null };
        });
    }


    series.push({
        name: "Min Temperature",
        type: "line",
        yAxisIndex: 1,
        stack: null,
        data: weatherDataCache.map(x => [x.dateAsDate, x.obMinTemp, x.distanceMeters]),
        symbol: "none",
        lineStyle: {
            color: "#24ead3" // line color
        },
        itemStyle: {
            color: "#24ead3" // dot color
        },
        emphasis: null,
        silent: true
    });

    series.push({
        name: "Max Temperature",
        type: "line",
        yAxisIndex: 1,
        stack: null,
        data: weatherDataCache.map(x => [x.dateAsDate, x.obMaxTemp, x.distanceMeters]),
        symbol: "none",
        lineStyle: {
            color: "orange" // line color
        },
        itemStyle: {
            color: "orange" // dot color
        },
        emphasis: null,
        silent: true
    });
    
    const { adjustedUsageSet, adjustedWeatherSet } = adjustProportionalScaling(series, isMultipleChart);
    
    const { min: minWeather, max: maxWeather } = adjustedWeatherSet;
    const { min: minUsage, max: maxUsage } = adjustedUsageSet;
        
    if (!isMultipleChart) {
        optionsCache.yAxis[0].min = parseFloat(minUsage.toFixed(0));
        optionsCache.yAxis[0].max = parseFloat(maxUsage.toFixed(0));
    }

    optionsCache.yAxis[1] = {
        scale: true, // min & max are set regardless
        type: 'value',
        name: "Temperature",
        min: Math.floor(minWeather), // round down
        max: Math.ceil(maxWeather), // round up
        nameTextStyle: {
            rich: {
                a: {
                    fontSize: '8px',
                    verticalAlign: 'top'
                },
                i: {
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                }
            }
        },
        axisLabel: {
            formatter: '{value} °C'
        },
        splitLine: {
            show: false
        }
    }

    legends.push("Min Temperature")
    legends.push("Max Temperature")
}

const getCostData = (
    accountServiceId: number,
    identifier: string,
    fromDate: string | null = null,
    toDate: string | null = null,
    isSmart: boolean,
    requireTestResponse: boolean) => {

    if (requireTestResponse) {
        costCache = [

            //new CostDetailModel("USAGE", "Peak Usage", new Date(2024, 1, 1), 10),
            //new CostDetailModel("USAGE", "Peak Usage", new Date(2024, 1, 2), 20),
            //new CostDetailModel("USAGE", "Peak Usage", new Date(2024, 1, 3), 30),
            //new CostDetailModel("USAGE", "Offpeak Usage", new Date(2024, 1, 1), 30),
            //new CostDetailModel("USAGE", "Offpeak Usage", new Date(2024, 1, 2), 30),
            //new CostDetailModel("USAGE", "Offpeak Usage", new Date(2024, 1, 3), 30),
            //new CostDetailModel("SOLAR", "Standard Solar Feed in", new Date(2024, 1, 1), -5),
            //new CostDetailModel("SOLAR", "Standard Solar Feed in", new Date(2024, 1, 2), -6),
            //new CostDetailModel("SOLAR", "Standard Solar Feed in", new Date(2024, 1, 3), -7),
            //new CostDetailModel("SUPPLY", "Daily Charge", new Date(2024, 1, 1), 5),
            //new CostDetailModel("SUPPLY", "Daily Charge", new Date(2024, 1, 2), 5),
            //new CostDetailModel("SUPPLY", "Daily Charge", new Date(2024, 1, 3), 5),
            //new CostDetailModel("SuperExport Top-Up", "SuperFeedIn", new Date(2024, 1, 1), -1.5),
            //new CostDetailModel("SuperExport Top-Up", "SuperFeedIn", new Date(2024, 1, 2), -1.6),
            //new CostDetailModel("SuperExport Top-Up", "SuperFeedIn", new Date(2024, 1, 3), -1.7),
            //new CostDetailModel("ZEROHERO Credit", "ZEROHERO", new Date(2024, 1, 1), -1),
            //new CostDetailModel("ZEROHERO Credit", "ZEROHERO", new Date(2024, 1, 2), -1),
            //new CostDetailModel("ZEROHERO Credit", "ZEROHERO", new Date(2024, 1, 3), -1),
        ]

        return new Promise<void>((resolve) => resolve());
    }
    
    return new Promise<void>((resolve) => {
        getCostDetail(new CostDetailRequest(accountServiceId, identifier, fromDate, toDate, isSmart))
                .then(res => res?.data
                    ? costCache = cloneDeep(res.data)
                    : costCache = cloneDeep(emptyCost))
            .catch(() => costCache = cloneDeep(emptyCost))
                .finally(() => resolve());
        });
}

const setUsageChartData = (isSmart: boolean, chartType:string, tg: TimeGranularityEnum) => {
    const lineCount = window.innerWidth <= 320 ? 1 : window.innerWidth <= 640 ? 2 : 4;
    const top = window.innerWidth <= 640 ? 40 : 30;
    const overUnit = window.innerWidth <= 640 ? 20 : 0;
    const buildSingle = (list: Array<MeterReadBasicModel> | Array<MeterReadSmartModel>) => {
        let [legends, series, specialEndMap] = dataProcesser(list, isSmart, tg);
        let lines = Math.floor(legends.length / lineCount) + 1;

        renderWeatherData(series, legends, tg, isSmart);
        
        optionsCache.legend = [{
            show: true,
            top: top,
            data: [...legends]
        } as Legend];
        optionsCache.grid.top = lines * 30 + top + overUnit;
        optionsCache.series = series;
        optionsCache.color = colorBuilder(series.length);
        optionsCache.tooltip.triggerOn = 'mousemove';
        setOptionsDateFormatter(tg, isSmart, chartType, specialEndMap);
    }

    const buildMultiple = (dic: Record<string, Array<MeterReadBasicModel>>) => {
        let totalLegends = new Array<Legend>();
        let totalSeriess = new Array<Series>();
        let lineWidth = 4;
        let size = 8;
        let opacity = 0.3;
        let ttop = top;
        for (let k in dic) {
            let [, series] = dataProcesser(dic[k], isSmart, tg);
            let newLegends = new Array<string>();
            // eslint-disable-next-line no-loop-func
            series.forEach(s => {
                let name = `${k}${qianhengxian(s.name)}`;
                let newSeries = {
                    name: name,
                    type: "line",
                    stack: null,
                    data: s.data,
                    lineStyle: {
                        width: lineWidth,
                        opacity: opacity
                    },
                    symbolSize: size,
                    emphasis: null,
                    silent: true,
                    itemStyle:null
                } as Series;
                newLegends.push(name);
                totalSeriess.push(newSeries);
            });
            totalLegends.push({
                show: true,
                top: ttop,
                data: [...newLegends]
            } as Legend);
            let lines = Math.floor(newLegends.length / lineCount) + 1;
            ttop += lines * 30;
            lineWidth -= 2;
            size -= 2;
            opacity += 0.7;
        }
        
        renderWeatherData(totalSeriess, totalLegends?.[0].data, tg, isSmart, true);
        
        let count = 0;
        totalLegends.forEach(x => count += x.data.length);
        if (count <= lineCount) {
            let t = totalLegends[0];
            let data = new Array<string>();
            totalLegends.forEach(x => data.push(...x.data));
            t.data = data;
            totalLegends = [t];
        }
        optionsCache.legend = totalLegends;
        optionsCache.grid.top = ttop + overUnit;
        optionsCache.series = totalSeriess;
        optionsCache.color = colorBuilder(totalSeriess.length);
        optionsCache.tooltip.triggerOn = 'mousemove';
        setOptionsDateFormatter(tg, isSmart, chartType);
    }
    if (isSmart) {
        optionsCache.xAxis.boundaryGap = ['0%', '0%'];
        buildSingle(smartReadCache);
    } else {
        optionsCache.xAxis.boundaryGap = ['2%', '2%'];
        buildMultiple(basicReadCache);
    }
    optionsCache.xAxis.min = null;
    optionsCache.xAxis.max = null;
}

const setCostChartData = (isSmart: boolean, chartType: string, tg: TimeGranularityEnum) => {
    const lineCount = window.innerWidth <= 320 ? 1 : window.innerWidth <= 640 ? 2 : 4;
    const top = window.innerWidth <= 640 ? 40 : 30;
    const overUnit = window.innerWidth <= 640 ? 20 : 0;

    const buildCost = (list: Array<CostDetailModel>, tg: TimeGranularityEnum) => {
        list.sort((a, b) => momentSort(a.date, b.date));
        let [legends, series] = costDataProcessor(list, tg);
        //console.log(legends)
        let lines = Math.floor(legends.length / lineCount) + 1;
        optionsCache.legend = [{
            show: true,
            top: top,
            data: [...legends]
        } as Legend];
        optionsCache.dataZoom = [];
        optionsCache.grid.top = lines * 30 + top + overUnit;
        optionsCache.series = series;
        optionsCache.color = colorMap(legends);
        optionsCache.yAxis[0].min = null;
        optionsCache.yAxis[0].max = null;
        //optionsCache.tooltip.triggerOn = "click";
        setOptionsDateFormatter(tg, isSmart, chartType);
    }
    //console.log(costCache);
    buildCost(costCache, tg);
}

const colorMap = (chargeTypes: Array<string>) => {
    let result = new Array<string>();
    chargeTypes.forEach((v) => {
        let color = ChargeTypeColorMap.get(v);
        if (color) {
            result.push(color);
        }
    });
    return result;
};

const fixTwoHex = (n: number) => ("0" + n.toString(16)).slice(-2);
const colorBuilder = (num: number) => {
    let parts = 0;
    for (let i = 2; ; i++) {
        let c = Math.pow(i, 3);
        if (c - i > num) {
            parts = i + 1;
            break;
        }
    }
    let count = Math.floor(255 / parts);
    let arr = [];
    for (let i = 1; ; i++) {
        let x = i * count;
        if (x > 255) {
            break;
        } else {
            arr.push(count * i)
        }
    }
    let rgb = {
        r: [...arr],
        g: [...arr],
        b: [...arr]
    }
    let color = [];
    let len = arr.length;
    for (let i = 0; i < len; i++) {
        for (let j = 0; j < len; j++) {
            for (let k = 0; k < len; k++) {
                if (k !== i && k !== j && j !== i) {
                    color.push("#" + fixTwoHex(rgb.r[i]) + fixTwoHex(rgb.g[j]) + fixTwoHex(rgb.b[k]));
                }
            }
        }
    }

    let clen = color.length;
    let skipCount = clen / num;
    let colorout = [];
    let skiped = 0;
    let skipindex = Math.floor(skiped * skipCount);
    while (skipindex < clen) {
        colorout.push(color[skipindex]);
        skiped++;
        skipindex = Math.floor(skiped * skipCount);
    }

    return colorout;
}

// const bindState = (stateMap: Map<string, [any, Function]>) => {
//     var obj = Object.create(null);
//     for (let key of Array.from(stateMap.keys())) {
//         let x = stateMap.get(key);
//         let copy = "__" + key;
//         Object.defineProperty(obj, copy, {
//             enumerable: false,
//             configurable: false,
//             writable: true,
//             value: x![0]
//         });
//         Object.defineProperty(obj, key, {
//             enumerable: true,
//             configurable: false,
//             // eslint-disable-next-line no-loop-func
//             get() { return obj[copy]; },
//             // eslint-disable-next-line no-loop-func
//             set(v) { obj[copy] = v; x![1](v); },
//         });
//     }
//     return obj;
// }
const DownLoadIcon = <FontAwesomeIcon
    icon={faDownload}
    style={{ margin: "0 3px", color: "#e71d73" }} />;

const DotLine = (props: {
    color: string,
    label: string
}) =>
    <div style={{ display: "inline-block", height: "16px", lineHeight: "16px", fontSize: "12px", verticalAlign: "top" }}>
        <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <div className="flex-solid" style={{ borderBottom: `2px solid ${props.color}`, width: "20px", height: "0px" }}></div>
            <div className="flex-solid" style={{ border: `2px solid ${props.color}`, width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#fff", marginLeft: "-15px" }}></div>
            <div style={{ marginLeft: "10px", color: "#e71d73" }}>{props.label}:</div>
        </div>
    </div>;

const ClickedBarCostInfo = (props: ClickedBarModel) => {
    const showVppItems = props.displaySolar === "";
    const items: { label: string, subLabel?: string, value: string, colorKey: string, vppOnly: boolean }[] = [
        { label: "Supply charge", value: `$${props.supplyCharge}`, colorKey: "SUPPLY", vppOnly: false },
        { label: "Energy cost", value: `$${props.energyCost}`, colorKey: "USAGE", vppOnly: false },
        { label: "ZEROHERO Credit", value: `$${props.zeroHero} cr`, colorKey: "ZEROHERO Credit", vppOnly: true },
        { label: "Solar feed-in", value: `$${props.solarFeedIn} cr`, colorKey: "SOLAR", vppOnly: true },
        { label: "Super Export top up", value: `$${props.superExport} cr`, colorKey: "Super Export top up", vppOnly: true },
        { label: "Critical Peak", subLabel: "(Export)", value: `$${props.criticalPeakExport} cr`, colorKey: "Critical Peak - Export", vppOnly: true },
        { label: "Critical Peak", subLabel: "(Import)", value: `$${props.criticalPeakImport} cr`, colorKey: "Critical Peak - Import", vppOnly: true },
    ];

    return (
        <div style={{ display: `${props.display}` }} >
            <div style={{ left: `${props.offsetX}px`, position: "relative", width: "0", height: "0", borderLeft: "15px solid transparent", borderRight: "15px solid transparent", borderBottom: "15px solid #E9EDF1" }}></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 40px", background: "#E9EDF1", boxShadow: "-3px -3px 10px 0px #CBCED2", borderBottomLeftRadius: "10px", borderBottomRightRadius: "10px" }}>
                <div style={{ paddingRight: "28px", borderRight: "1px solid #d5dae0" }}>
                    <div style={{ fontSize: "smaller", color: "darkgrey" }}>{props.date}</div>
                    <div style={{ fontWeight: "bolder", marginTop: "2px", whiteSpace: "nowrap" }}>Total cost:</div>
                    <div style={{ fontWeight: "bolder", whiteSpace: "nowrap" }}>${props.totalCost}</div>
                </div>
                {items
                    .filter(item => !item.vppOnly || showVppItems)
                    .map(item => (
                        <div key={item.colorKey} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                            <div style={{ width: "30px", height: "30px", borderRadius: "15px", marginBottom: "4px", backgroundColor: ChargeTypeColorMap.get(item.colorKey) }}></div>
                            <div style={{ fontSize: "smaller", color: "darkgrey", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                                {item.label}
                                {item.subLabel && <><br />{item.subLabel}</>}
                            </div>
                            <div style={{ fontWeight: "bolder", marginTop: "2px", whiteSpace: "nowrap" }}>{item.value}</div>
                        </div>
                    ))}
            </div>
        </div>
    );
}


const UsageCostChart = () => {
    const currentAccount = useSelector(selectCurrentAccount);
    const chartRef = useRef<ReactECharts | null>(null);
    const [siteList, setSiteList] = useState<Site[]>([]);
    const [currentAccountService, setCurrentAccountService] =
        useState<AccountServiceInfo | null>(null);
    const [meterList, setMeterList] = useState<MeterReadMeter[]>();
    const [currentMeterId, setCurrentMeterId] = useState<string>();
    const [currentTimeGranularity, setCurrentTimeGranularity] = useState(TimeGranularity.Usage.BASIC);
    const [currentChartType, setCurrentChartType] = useState<string>(ChartTypeEnum.Usage);
    const [timeGranularityIndex, setTimeGranularityIndex] = useState(0);
    const [currentDateRange, setCurrentDateRange] = useState(emptyRange)
    const [options, setOptions] = useState(cloneDeep(DefaultOptions));
    const [showActualTip, setShowActualTip] = useState(false);
    const [currentMeterType, setCurrentMeterType] = useState("");
    const [meterReadType, setMeterReadTypes] = useState<Record<string,string>>();
    const [disableDateArr, setDisableDateArr] = useState({ frmpdate: null as Moment | null, closedate: null as Moment | null });
    const [clickedBarValue, setClickedBarValue] = useState(cloneDeep(DefaultClickedBarModel));

    const disableAll = (disableDateArr.closedate as any) < (disableDateArr.frmpdate as any);

    const navigate = useNavigate();

    const meterLabelText = (meter: MeterReadMeter) => {
        let currentMeterReadType;
        if(meter.meterReadType === MeterType.BASIC){
            currentMeterReadType = meter.meterReadType;
        } else {
            currentMeterReadType = meterReadType ? (meterReadType[meter.serialNumber] ?? meter.meterReadType) : meter.meterReadType;
        }
        const displayStatus = meter.serialStatus ? METER_STATUS_DISPLAY_MAP[meter.serialStatus] || meter.serialStatus : '';
        const meterStatus = displayStatus ? ` (${displayStatus})` : '';
        return `${currentMeterReadType.toUpperCase()} METER - ${meter.serialNumber}${meterStatus}`;   
    }

    if (window.innerWidth <= 640) {
        optionsCache.xAxis.axisLabel.rotate = -90;
        optionsCache.tooltip.position = ['0%', '100%'];
    } else {
        optionsCache.xAxis.axisLabel.rotate = 0;
        optionsCache.tooltip.position = null;
    }

    const buildChart = (isSmart: boolean, tg: TimeGranularityEnum, chartType: string) => {
        if (chartType === ChartTypeEnum.Usage) {
            setUsageChartData(isSmart, chartType, tg);
            if (basicReadCache[BasicMeterReadDataType.ActualReads].length > 0) {
                setShowActualTip(true);
            } else {
                setShowActualTip(false);
            }
        }
        else {
            setCostChartData(isSmart, chartType, tg);
        }

        optionsCache.yAxis[0].scale = !optionsCache.series.some(s => s.type === "bar");

        //console.log(optionsCache)
        //console.log(JSON.stringify(optionsCache))
        setOptions(cloneDeep(optionsCache));
    }

    const downLoadCsvFileLocal = () => {

        let meter = meterList?.find(m => m.meterId === currentMeterId);
        let site = siteList.find(s => s.accountServiceInfo === currentAccountService);
        let pt = getPickerType(currentTimeGranularity[timeGranularityIndex]);

        if (!meter || !site) {
            return;
        }

        downLoadCsvFile(
            site.accountServiceInfo.accountServiceId,
            buildRequest(
                site.nmi,
                meter, pt,
                currentDateRange,
                checkIsCrossAccountService(currentAccountService!)));
    }

    const siteSelected = (accountService: AccountServiceInfo | null) => {
        setCurrentAccountService(accountService);
        setCurrentChartType(ChartTypeEnum.Usage);
    }

    const serialSelected = (meterId: string | undefined) => {
        setCurrentMeterId(meterId);
    }

    const rangeSelected = (range: RangeValue<moment.Moment>) => {
        if (!range) {
            return;
        }

        hasUserSelectedDate = true;
        setCurrentDateRange([
            range[0] ? toUtcStartOfDay(range[0]) : null,
            range[1] ? toUtcStartOfDay(range[1]) : null]);
    }

    const chartTypeChanged = (e: RadioChangeEvent) => {
        setCurrentChartType(e.target.value);
        let chartType = e.target.value as ChartTypeEnum;
        let curTimeGranularityIndex = 0; let meter = meterList?.find(m => m.meterId === currentMeterId);
        let site = siteList.find(s => s.accountServiceInfo === currentAccountService);
        if (!meter || !site) {
            setCurrentTimeGranularity(TimeGranularity.Usage.BASIC);
            setTimeGranularityIndex(0)
            return;
        }
        let isSmart = meter.meterReadType === MeterType.SMART;
        if (isSmart) {
            if (timeGranularityIndex === 0) {
                curTimeGranularityIndex = chartType === ChartTypeEnum.Cost ? 1 : 2;
            }
            else
            {
                if (chartType === ChartTypeEnum.Cost) {
                    curTimeGranularityIndex = timeGranularityIndex > 2 ? 1 : timeGranularityIndex - 1
                }
                else {
                    curTimeGranularityIndex = timeGranularityIndex + 1
                }
            }
        }
        else {
            curTimeGranularityIndex = 0;
        }
        setTimeGranularityIndex(curTimeGranularityIndex); 
    };

    const timeGranularitySelected = (index: number) => {
        setTimeGranularityIndex(index);
    }

    const legendselectchanged = useCallback(() => { }, []);
    const datazoom = useCallback(() => { dateCache = null }, []);
    const dataClicked = useCallback((params: any) => {
        let clickedBarX = [0];
        const y = params.value[1]; 

        if (chartRef.current) {
            const chartInstance = chartRef.current.getEchartsInstance();
            clickedBarX = chartInstance.convertToPixel({ seriesIndex: params.seriesIndex }, [params.data[0],y]);
        }
        let currentDimensionDataMap = new Map<string, number>();
        optionsCache.series.forEach((s) => {
            let costData = s.data.find((d) => d[0].getTime() === params.data[0].getTime());
            if (costData) {
                currentDimensionDataMap.set(s.name, costData[1]);
            }
            else {
                currentDimensionDataMap.set(s.name, 0);
            }
        });

        let energy = currentDimensionDataMap.get("USAGE") ?? 0;
        let supply = currentDimensionDataMap.get("SUPPLY") ?? 0;
        let solar = Math.abs(currentDimensionDataMap.get("SOLAR") ?? 0);
        let superExport = Math.abs(currentDimensionDataMap.get("Super Export top up") ?? 0);
        let zeroHero = Math.abs(currentDimensionDataMap.get("ZEROHERO Credit") ?? 0);
        let criticalPeakExport = Math.abs(currentDimensionDataMap.get("Critical Peak - Export") ?? 0);
        let criticalPeakImport = Math.abs(currentDimensionDataMap.get("Critical Peak - Import") ?? 0);
        let value = {
            offsetX: clickedBarX[0] - 14,
            date: safeMoment(params.data[0]).format(DefaultDisplayDateFormat),
            energyCost: energy,
            supplyCharge: supply,
            solarFeedIn: solar,
            superExport: superExport,
            zeroHero: zeroHero,
            criticalPeakExport: criticalPeakExport,
            criticalPeakImport: criticalPeakImport,
            totalCost: Number((energy + supply - solar - superExport - zeroHero - criticalPeakExport - criticalPeakImport).toFixed(2)),
            display: "block",
            displaySolar: currentAccountService!.siteIdentifier.startsWith("5") ? "none" : ""
        } as ClickedBarModel;
        setClickedBarValue(value);
    }, [currentAccountService])


    const getSiteTpye = () => {
        let site = siteList.find(s => s.accountServiceInfo === currentAccountService);

        if (!site) {
            return '';
        }

        return site.nmi.startsWith("5")
            ? "Gas"
            : "Electricity";
    }

    const setDatePicker = (
        daterange: RangeValue<moment.Moment> | null,
        frmprange: RangeValue<moment.Moment>) => {

        setCurrentDateRange(daterange);
        setDisableDateArr({ frmpdate: frmprange![0], closedate: frmprange![1] });
    }
    
    const findCurrentPostCode = () => {
        return currentAccount?.services?.find(x => 
            x.siteIdentifier === currentAccountService?.siteIdentifier)?.postCode || "";
    }

    useEffect(() => {
        optionsCache = cloneDeep(DefaultOptions);
        hasUserSelectedDate = false;
        accountServiceFrmpDateMap.map = null
        accountServiceFrmpDateMap.callback = null
    }, [])

    useEffect(() => {

        if (!currentAccount?.services?.length) {
            return;
        }

        accountServiceFrmpDateMap.map = null;

        const sortSiteList = (siteList: Site[]) => {
            return siteList.sort((site1, site2) => {
                // reverse(): to make sure the service with status not in the list is at the bottom
                const allServiceStatus = Object.keys(AccountServiceStatusForDisplay).reverse();
                const index1 = allServiceStatus.indexOf(site1.accountServiceInfo.status);
                const index2 = allServiceStatus.indexOf(site2.accountServiceInfo.status);

                return index2 - index1;
            })
        }

        const siteList = toSite(currentAccount.services)
        const sortedSiteList = sortSiteList(siteList)
        setSiteList(sortedSiteList);

        const action = async () => {

            const missingSIds = currentAccount.services
                .map(acs => acs.allAccountServiceIds)
                .flat()
                .filter(id => !accountServiceFrmpDateMap.map?.[id]);

            if (!missingSIds.length) {
                accountServiceFrmpDateMap.callback?.(accountServiceFrmpDateMap.map);

                return;
            }

            const res = await getAccountServiceStatusDateAsync(
                currentAccount.services.map(acs => acs.allAccountServiceIds).flat());

            if (!res?.data) {
                return;
            }

            accountServiceFrmpDateMap.map = { ...accountServiceFrmpDateMap.map, ...res.data };

            accountServiceFrmpDateMap.callback?.(accountServiceFrmpDateMap.map);
        }

        action();

    }, [currentAccount]);

    useEffect(() => {
        let activeSites: Site[] = [];

        if (siteList.length) {
            activeSites = siteList.filter(s => activeServiceStatus.includes(s.accountServiceInfo.status as EuropaAccountServiceStatus));
        };

        activeSites.length === 1 ? siteSelected(activeSites[0].accountServiceInfo) : siteSelected(null);
    }, [siteList])

    useEffect(() => {
        let site = siteList.find(
            s => s.accountServiceInfo === currentAccountService);

        accountServiceFrmpDateMap.callback = null;

        if (site && currentAccountService) {

            let nmi = site.nmi;
            if (nmi) {
                nmi.startsWith("5")
                    ? optionsCache.yAxis[0].name = "{i|M}{a| 3}"
                    : optionsCache.yAxis[0].name = "kWh";
            } else {
                optionsCache.yAxis[0].name = "";
            }

            requestCache.meterSeted = false;

            const getSortedMeterList = (meterList: MeterReadMeter[]) => {
                // show Turnedon and Energized first
                return meterList.sort((meter1, meter2) => {
                    return activeMeterStatus.indexOf(meter2.serialStatus as ActiveMeterStatus)
                        - activeMeterStatus.indexOf(meter1.serialStatus as ActiveMeterStatus);
                })
            }

            if (site.accountServiceInfo.serviceType === ServiceType.Power) {
                getPowerMeterTypes(site.nmi)
                    .then(res => {
                        if (res?.data) {
                            setMeterReadTypes(res.data);
                        }
                    });
            } else {
                setMeterReadTypes(undefined);
            }

            getMeterListFromRead(currentAccountService.accountServiceId)
                .then(res => {
                    if (res?.data?.length) {
                        setMeterList(getSortedMeterList(res.data))
                    } else {
                        setMeterList(undefined);
                    }
                });
            // .catch(e => console.log(e));
        } else {
            setMeterList(undefined);
        }

        let map = accountServiceFrmpDateMap.map;

        let setFrmpRange = (map: Record<number, AccountServiceStatusDateDto>) => {

            requestCache.rangeSeted = false;

            const allFrmpAndCloseDate = Object.keys(map)
                .filter(k => currentAccountService?.allAccountServiceIds
                    .some(aId => aId === parseInt(k)))
                .map((k) => ({
                    frmpDate: map[parseInt(k)].frmpDate,
                    accountClosedDate: map[parseInt(k)].accountClosedDate
                }));

            // console.log(allFrmpAndCloseDate)

            let frmp = (allFrmpAndCloseDate
                .sort((a, b) => ((a.frmpDate as Moment | null)?.unix() ?? Number.MAX_SAFE_INTEGER) -
                    ((b.frmpDate as Moment | null)?.unix() ?? Number.MAX_SAFE_INTEGER))
            [0]?.frmpDate ?? null) as Moment | null; // min frmp date or null

            let end = (allFrmpAndCloseDate
                .sort((a, b) => ((b.accountClosedDate as Moment | null)?.unix() ?? Number.MAX_SAFE_INTEGER) -
                    ((a.accountClosedDate as Moment | null)?.unix() ?? Number.MAX_SAFE_INTEGER))
            [0]?.accountClosedDate ?? EndOfTodayMoment) as Moment; // max close date or today

            // console.log(frmp,end)

            if (!frmp) {
                setDatePicker(null, [today(), today().add(-10, 'day')]); // disable all dates if frmp is null

                return;
            }

            let daterange = null as RangeValue<moment.Moment> | null;

            const setRangeWithDefault = () => {

                let fourteenMonthBeforeEnd = moment(end).add(-14, "months");

                let start = frmp!.isBefore(fourteenMonthBeforeEnd)
                    ? fourteenMonthBeforeEnd
                    : frmp;

                daterange = [start, end];
            }

            if (!hasUserSelectedDate) {

                setRangeWithDefault();

            } else if (currentDateRange) {

                if (frmp.isAfter(currentDateRange[1]) || end.isBefore(currentDateRange[0])) {

                    setRangeWithDefault();

                } else {

                    let tomoment = end.isAfter(currentDateRange[1])
                        ? currentDateRange[1]?.isBefore(frmp!)
                            ? end
                            : currentDateRange[1]
                        : end;
                    let startmoment = frmp.isBefore(currentDateRange[0])
                        ? currentDateRange[0]?.isAfter(tomoment)
                            ? frmp
                            : currentDateRange[0]
                        : frmp;
                    daterange = [startmoment, tomoment];

                }
            } else {

                setRangeWithDefault();

            }

            setDatePicker(daterange, [frmp, end]);
        }

        if (map) {
            setFrmpRange(map);
        } else {
            accountServiceFrmpDateMap.callback = setFrmpRange;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAccountService])

    useEffect(() => {
        if (!meterList) {
            serialSelected(undefined);
            return;
        }

        if (meterList[0].meterId === currentMeterId) {
            requestCache.meterSeted = true;
            doRequest(currentChartType);
        } else {
            let activeMeters: MeterReadMeter[] = meterList.filter(meter => activeMeterStatus.includes(meter.serialStatus as ActiveMeterStatus));
            activeMeters.length === 1 ? serialSelected(activeMeters[0].meterId) : serialSelected(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meterList])

    const doRequest = (chartType: string) => {
        let meter = meterList?.find(m => m.meterId === currentMeterId);
        let site = siteList.find(s => s.accountServiceInfo === currentAccountService);

        // clear weather Axis cache
        // @ts-ignore
        optionsCache.yAxis[1] = {};
        weatherDataCache = [];

        if (!meter || !site) {
            setCurrentTimeGranularity(TimeGranularity.Usage.BASIC);

            return;
        }

        let isSmart = meter.meterReadType === MeterType.SMART;
        let curTimeGranularity = chartType === ChartTypeEnum.Cost ? (isSmart ? TimeGranularity.COST.SMART
            : TimeGranularity.COST.BASIC)
            : (isSmart ? TimeGranularity.Usage.SMART
                : TimeGranularity.Usage.BASIC);

        let tg = curTimeGranularity[timeGranularityIndex] as TimeGranularityEnum;
        if (!requestCache.meterSeted || !requestCache.rangeSeted) {
            basicReadCache = emptyBasicReads;
            smartReadCache = emptySmartReads;
            buildChart(isSmart, tg, chartType);
            return;
        }
        let pt = getPickerType(tg);
        if (currentTimeGranularity !== curTimeGranularity) {
            setCurrentTimeGranularity(curTimeGranularity);
            if (chartType === ChartTypeEnum.Usage) {
                pt = TimeGranularityEnum.date;
                tg = TimeGranularityEnum.Day;
            }
        }
        let center = window.innerWidth > 640 ? " - " : "\n";
        optionsCache.title.text = `${site.nmi}${center}${meterLabelText(meter)}`;

        isSmart
            ? setCurrentMeterType(MeterType.SMART)
            : setCurrentMeterType(MeterType.BASIC);
        if (chartType === ChartTypeEnum.Usage) {
            if (!meter || !site) {
                return;
            }

            getMeterData(
                site.accountServiceInfo.accountServiceId,
                buildRequest(
                    site.nmi,
                    meter,
                    pt,
                    currentDateRange,
                    checkIsCrossAccountService(currentAccountService!)),
                disableAll)
            .then(() => {
                const hasSmartReads = smartReadCache?.length > 0;
                const hasInvoiceReads = basicReadCache?.[BasicMeterReadDataType.InvoiceReads]?.length > 0;
                const hasActualReads = basicReadCache?.[BasicMeterReadDataType.ActualReads]?.length > 0;

                if (hasSmartReads || hasInvoiceReads || hasActualReads) {
                    return getWeatherDataRequest(
                        currentAccountService?.accountServiceId || 0,
                        currentDateRange,
                        findCurrentPostCode()
                    );
                }
                return null;
            })
            .finally(() => {
                buildChart(isSmart, tg, chartType);
            });
        }
        else
        {
            let from = currentDateRange ? currentDateRange[0] ? currentDateRange[0]!.startOf(pt).format(DateFormatYMD) : null : null;
            let to = currentDateRange ? currentDateRange[1] ? currentDateRange[1]!.endOf(pt).format(DateFormatYMD) : null : null;
            //console.log(from);
            //console.log(to);
            getCostData(site.accountServiceInfo.accountServiceId, site.nmi, from, to, isSmart, false)
                .then(() => buildChart(isSmart, tg, chartType));
        }
    }

    useEffect(() => {
        if (currentChartType === ChartTypeEnum.Usage) {
            requestCache.meterSeted = true;
            doRequest(currentChartType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMeterId])

    useEffect(() => {
        requestCache.rangeSeted = true;
        doRequest(currentChartType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDateRange, disableDateArr])

    useEffect(() => {
        let meter = meterList?.find(m => m.meterId === currentMeterId);
        if (meter) {
            if (currentChartType === ChartTypeEnum.Usage) {
                setTimeGranularityIndex(meter.meterReadType === MeterType.SMART ? 2 : 0);
            }
        } else {
            setTimeGranularityIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTimeGranularity])

    useEffect(() => {
        let meter = meterList?.find(m => m.meterId === currentMeterId);
        if (meter) {
            buildChart(meter.meterReadType === MeterType.SMART, currentTimeGranularity[timeGranularityIndex] as TimeGranularityEnum, currentChartType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeGranularityIndex])

    const changeDefaultTimeGranularity = () => {
        let meter = meterList?.find(m => m.meterId === currentMeterId);
        let site = siteList.find(s => s.accountServiceInfo === currentAccountService);

        if (!meter || !site) {
            setCurrentTimeGranularity(TimeGranularity.Usage.BASIC);

            return;
        }

        let isSmart = meter.meterReadType === MeterType.SMART;
        let curTimeGranularity = currentChartType === ChartTypeEnum.Cost ? (isSmart ? TimeGranularity.COST.SMART
            : TimeGranularity.COST.BASIC)
            : (isSmart ? TimeGranularity.Usage.SMART
                : TimeGranularity.Usage.BASIC);

        if (currentTimeGranularity !== curTimeGranularity) {
            setCurrentTimeGranularity(curTimeGranularity);
        }
    }


    useEffect(() => {
        changeDefaultTimeGranularity()
        doRequest(currentChartType);
        setClickedBarValue(DefaultClickedBarModel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentChartType])

    useEffect(() => {
        let site = siteList.find(
            s => s.accountServiceInfo === currentAccountService);

        if (site && currentAccountService) {
            let nmi = site.nmi;
            if (nmi) {
                if (currentChartType === ChartTypeEnum.Usage) {
                    nmi.startsWith("5")
                        ? optionsCache.yAxis[0].name = "{i|M}{a| 3}"
                        : optionsCache.yAxis[0].name = "kWh";
                }
                else {
                    optionsCache.yAxis[0].name = "$";
                }
            } else {
                optionsCache.yAxis[0].name = "";
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentChartType, currentAccountService])

    return (
        <div className={styles["usage-chart-block"]}>
            <div className="globird-menu-option">
                <p className="globird-menu-label">Show me: </p>
                <MenuOptions
                    id="usageChartMenuSelector"
                    defaultIndex={0} />
            </div>
            <div className={styles["usagechart-item"]}>
                <p>Select Your Gas/Electricity Service</p>
                <SiteSelector
                    onChange={s => s && siteSelected(s.accountServiceInfo)}
                    sites={siteList}
                    currentSite={siteList?.find(s => s.accountServiceInfo === currentAccountService)}
                    className={styles["usagechart-item-select"]}
                />
                {/* <Select
                    className={styles["usagechart-item-select"]}
                    onChange={siteSelected}
                    value={currentAccountServiceId}>
                    {
                        siteList.map(site => (
                            <Option key={site.accountServiceId} value={site.accountServiceId}><i style={{ padding: "0 5px" }} className={`fa fa-${site.nmi.startsWith("5") ? "fire" : "bolt"}`}></i>{`${site.nmi.startsWith("5") ? "Gas" : "Pow"} - ${site.nmi} - ${site.address}`}</Option>
                        ))
                    }
                </Select> */}
            </div>
            {
                currentAccountService ?
                    <div className={styles["usagechart-item"]}>
                        <p>Select Your {getSiteTpye()} Meter</p>
                        <Select className={styles["usagechart-item-select"]} onChange={v => serialSelected(v)} value={currentMeterId} disabled={currentChartType === ChartTypeEnum.Cost}>
                            {
                                meterList?.map(meter => (
                                    <Option key={meter.meterId} value={meter.meterId}>{meterLabelText(meter)}</Option>
                                ))
                            }
                        </Select>
                    </div>
                    : null
            }
            {
                currentMeterId &&
                <>
                    <div className={styles["usagechart-item"]}>
                        <p>Select Usage Period</p>
                        <div
                            className='flex-left-center'>
                            <Select className={styles["usagechart-item-select"]} onChange={v => timeGranularitySelected(v)} value={timeGranularityIndex}>
                                {
                                    currentTimeGranularity.map((tg, index) => (
                                        <Option key={index} value={index}>{tg}</Option>
                                    ))
                                }
                            </Select>
                            <RangePicker
                                picker={getPickerType(currentTimeGranularity[timeGranularityIndex])}
                                value={currentDateRange}
                                onChange={rangeSelected}
                                disabledDate={date => shouldBeDisabledByDayDateRange(
                                    date,
                                    disableDateArr.frmpdate,
                                    disableDateArr.closedate)}
                                format={getPickerFormat(currentTimeGranularity[timeGranularityIndex])} />
                            <Radio.Group className={styles["usagechart-radiobutton"]} buttonStyle="solid" value={ currentChartType } defaultValue={ChartTypeEnum.Usage} onChange={chartTypeChanged}>
                                <Radio.Button className={styles["usagechart-radiobutton-left-radius"]} value={ ChartTypeEnum.Usage }>Usage(kWh)</Radio.Button>
                                <Radio.Button className={styles["usagechart-radiobutton-right-radius"]} value={ ChartTypeEnum.Cost }>Cost($)</Radio.Button>
                            </Radio.Group>
                            
                        </div>
                    </div>
                    {
                        currentChartType === ChartTypeEnum.Usage ?
                            <p style={{ width: "100%", textAlign: "right" }}>
                                <FontAwesomeIcon
                                    icon={faDownload}
                                    title="download meter read summary"
                                    onClick={disableAll ? undefined : downLoadCsvFileLocal}
                                    style={{
                                        cursor: disableAll ? 'not-allowed' : 'pointer',
                                        marginRight: '30px',
                                        color: disableAll ? 'gray' : '#e71d73'
                                    }}
                                />
                            </p>
                        :<></>
                    }
                    <ReactECharts ref={chartRef} lazyUpdate={false} notMerge={true} className={styles["usagechart-chart"]} option={options} onEvents={{ "legendselectchanged": legendselectchanged, "datazoom": datazoom, "click": dataClicked }} />
                    {
                        currentChartType === ChartTypeEnum.Usage ?
                        currentMeterType === MeterType.BASIC
                            ? <div style={{ fontSize: "12px", lineHeight: "16px" }}>
                                <p style={{ margin: "10px 0" }}>
                                    <DotLine color={optionsCache.color.length <= 2 && showActualTip ? optionsCache.color[0] : "#000"} label="Invoice Read Data" /> Invoice Read Data shows the meter reads used to calculate your bills. We prefer to bill you based on actual reads. On bills where an actual meter read is not available GloBird may bill you using an estimate, and then adjust your next bill accordingly when an actual read is received.
                                    {
                                        showActualTip && getSiteTpye() === "Gas"
                                            ? <span> To learn more about gas estimates click <a target="_blank" href={links.corporateWebsite.estimation} rel="noreferrer">here</a></span>
                                            : <></>
                                    }
                                </p>
                                {
                                    showActualTip
                                        ? <p style={{ margin: "10px 0" }}><DotLine color={optionsCache.color.length === 2 ? optionsCache.color[1] : "#000"} label="Distributor Read/Self Read Data" /> Distributor Read Data shows all the meter reads from distributor, including actual and estimated meter reads. Self Read Data shows the reads you provided to us.</p>
                                        : <></>
                                }
                                <p style={{ margin: "10px 0" }}>{DownLoadIcon}: Click {DownLoadIcon} button to view meter read summary. It includes: read date, read type (actual/estimate) and read index.</p>
                            </div>
                                : <></>
                            :
                            <ClickedBarCostInfo {...clickedBarValue } />
                    }
                    { 
                        currentChartType === ChartTypeEnum.Usage ?
                            <p style={{ textAlign: 'justify' }}>
                                Depending on your meter configuration, you could be paying a different price for power depending on when you use it. See what time of the day your meter is on Peak, Shoulder or Off-Peak. You might save money by using power in the Off-Peak times. Please note all times shown here are displayed in your local time.<br/>
                                More information <a href={links.corporateWebsite.tariffTimetables}>here</a>.
                            </p>
                            :
                            <p style={{ textAlign: 'justify' }}>
                                Costs include GST. They are indicative only. Always check your bill for the amount payable and due dates. Please note; It's sometimes possible for meter data to be updated or adjusted at a future date. The estimated costs do not factor in things like concessions, government rebates that might apply, or any account credits. However, you can see those on your bill. 
                            </p>
                    }
                </>
            }
        </div>
    );
}

export default UsageCostChart;
